import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

// Referrals for the "Share the app" modal — the caller's successful referrals plus their own
// attributed invite link, read from /api/referrals (the web counterpart of the native `referrals`
// read, so both surfaces report the same joined count, earned runs and link).

export interface Referral {
  id: string;
  name: string;
}

export interface ReferralsState {
  referrals: Referral[];
  // '' until we know it — a caller with no metasite has nothing to attribute an invite to. Callers
  // must refuse to offer a copy of an empty link rather than hand out one that credits nobody
  // (the rule iOS states on its own share CTA).
  shareLink: string;
  // The invite's 8-char public short code, '' until we know it. `shareLink` above is built FROM it when
  // present (the short `…/r/<code>` form; the metasite long form is the code-less fallback). It is
  // exposed here because no client can derive the code — it is a SecureRandom value on the recipient row,
  // so it has to arrive from the server.
  referralCode: string;
  loading: boolean;
  error: boolean;
}

interface ReferralsResponse {
  referrals?: Referral[];
  shareLink?: string;
  referralCode?: string;
  // The route answers 200 with the link even when the LIST read fails, because the invite is the point of
  // the screen and losing it to a list timeout would take the feature down. This distinguishes that case
  // from a genuinely empty list, which the modal states as "nothing earned yet".
  listFailed?: boolean;
}

export function useReferrals({ enabled = true }: { enabled?: boolean } = {}): ReferralsState {
  const query = useQuery({
    queryKey: queryKeys.referrals,
    enabled,
    queryFn: async (): Promise<ReferralsResponse> => {
      const res = await apiFetch(`${API_BASE}/api/referrals`, { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as ReferralsResponse;
    },
  });

  return {
    referrals: query.data?.referrals ?? [],
    shareLink: query.data?.shareLink ?? '',
    referralCode: query.data?.referralCode ?? '',
    // A disabled query sits in 'loading' forever with no fetch in flight, so ask fetchStatus too or the
    // closed modal reports a load that never started.
    loading: query.status === 'loading' && query.fetchStatus !== 'idle',
    error: query.status === 'error' || query.data?.listFailed === true,
  };
}
